// Proactively prompts a customer to review their most recent delivered,
// unreviewed order — the reviews table/UI already existed, but nothing ever
// surfaced the ask; customers had to remember to visit My Orders themselves.
import { useState, useMemo } from "react";
import { Star, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useMyOrders, useMyReviews } from "@/lib/firestoreService";
import { ReviewModal } from "@/pages/MyOrdersPage";
import { orderNumber } from "@/lib/orderNumber";

const DISMISS_KEY = "review_nudge_dismissed";

const getDismissed = () => {
  try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; }
};

export const ReviewNudge = () => {
  const { user, profile } = useAuth();
  const orders = useMyOrders(user?.id);
  const myReviews = useMyReviews(user?.id);
  const [dismissedIds, setDismissedIds] = useState(getDismissed);
  const [reviewOrder, setReviewOrder] = useState(null);

  // Most recently delivered order that's neither reviewed nor dismissed
  // this session — re-prompts on the next visit if still unreviewed.
  const target = useMemo(() => {
    if (!user || !orders) return null;
    return [...orders]
      .filter(o => o.status === "delivered" && !myReviews[o.id] && !dismissedIds.includes(o.id))
      .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0))[0] || null;
  }, [user, orders, myReviews, dismissedIds]);

  if (!user) return null;

  const dismiss = (id) => {
    const next = [...dismissedIds, id];
    setDismissedIds(next);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };

  return (
    <>
      {target && !reviewOrder && (
        <div
          data-testid="review-nudge"
          className="fixed bottom-5 left-5 z-50 w-72 bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-2xl p-4"
          style={{ animation: "nudge-pop 0.3s ease-out" }}
        >
          <button
            onClick={() => dismiss(target.id)}
            className="absolute top-2 right-2 text-[#5A2D1D]/40 hover:text-[#5A2D1D]"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <Star className="w-4 h-4 text-[#556B2F] fill-[#556B2F]" />
            <span className="font-display font-bold text-[#5A2D1D] text-sm">How was your order?</span>
          </div>
          <p className="text-xs text-[#2E1A12]/70 mb-3">
            Order {orderNumber(target)} was delivered — tell us how it went!
          </p>
          <div className="flex gap-2">
            <button
              data-testid="review-nudge-rate"
              onClick={() => setReviewOrder(target)}
              className="flex-1 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] text-xs font-bold py-2 rounded-full transition-colors"
            >
              Rate Now
            </button>
            <button
              onClick={() => dismiss(target.id)}
              className="text-xs font-semibold text-[#2E1A12]/60 hover:text-[#2E1A12] px-2"
            >
              Later
            </button>
          </div>
          <style>{`
            @keyframes nudge-pop {
              0% { transform: translateY(16px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {reviewOrder && (
        <ReviewModal order={reviewOrder} user={user} profile={profile} onClose={() => setReviewOrder(null)} />
      )}
    </>
  );
};
